import { Component, ChangeDetectionStrategy, OnInit, ChangeDetectorRef, signal, inject } from '@angular/core';
import { FileItem, FileUploadModule } from 'ng2-file-upload';
import { IrisFilesService } from '@iris/common/modules/dms/services/files.service';
import { ToastService } from '@ngx-strakit/cdk/controls/toast';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import some from 'lodash/some';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FileUploadState } from '@iris/common/modules/dms/models/IrisDMSFile';
import { IrisDmsFilesSandbox } from '@iris/common/modules/dms/sandbox/dms-files.sandbox';
import { IrisDmsFileUploadHelper } from '@iris/common/modules/dms/helpers/dms-file-upload.helper';
import { StrakitButtonComponent, StrakitTextComponent } from '@ngx-strakit/cdk/controls';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IrisFileSizePipe } from '@iris/common/modules/pipes-common/pipes';
import { UpperCasePipe } from '@angular/common';
import {
  ConfirmByKeypressDirective,
  SkModalBase,
  SkModalContentComponent,
  StrakitButtonPrefixDirective,
} from '@ngx-strakit/cdk';

@Component({
  selector: 'iris-import-zip-folder-modal',
  imports: [
    ReactiveFormsModule,
    UpperCasePipe,
    TranslatePipe,

    MatTooltipModule,
    FileUploadModule,

    StrakitTextComponent,

    ConfirmByKeypressDirective,
    SkModalContentComponent,
    IrisFileSizePipe,
    StrakitButtonComponent,
    StrakitButtonPrefixDirective,
  ],
  templateUrl: './import-zip-folder-modal.component.html',
  styleUrls: ['./import-zip-folder-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    IrisDmsFileUploadHelper,
  ],
})
export class IrisImportZipFolderModalComponent extends SkModalBase<IrisImportZipFolderModalComponent, void> implements OnInit {
  private readonly filesService = inject(IrisFilesService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly dmsFilesSandbox = inject(IrisDmsFilesSandbox);
  private readonly dmsFileUploadHelper = inject(IrisDmsFileUploadHelper);

  private readonly folderId = signal<string>(inject<{ folderId: string }>(MAT_DIALOG_DATA).folderId);

  readonly uploader = this.dmsFileUploadHelper.zipFileUploader;
  readonly folderCommentControl = new FormControl<string>('');

  get acceptMimeTypes(): string {
    return this.uploader?.options.allowedMimeType?.join(',');
  }

  ngOnInit(): void {
    this.uploader.setOptions({
      url: this.filesService.getZipImportUrl(this.folderId()),
    });

    // for progress bar updating
    this.uploader.onSuccessItem = (item: FileItem) => {
      this.dmsFilesSandbox.updateUploadingFileState(item.file.name, FileUploadState.Completed);
      this.dmsFilesSandbox.addUploadingFileFolder(item.file.name, this.folderId());
      this.changeDetector.detectChanges();
      item.remove();
    };

    this.uploader.onErrorItem = (item: FileItem) => {
      this.dmsFilesSandbox.updateUploadingFileState(item.file.name, FileUploadState.Error);
      item.isUploaded = false;
    };

    this.uploader.onCancelItem = (item: FileItem) => {
      this.dmsFilesSandbox.updateUploadingFileState(item.file.name, FileUploadState.Canceled);
      item.remove();
    };

    this.uploader.onCompleteAll = () => {
      if (!some(this.uploader.queue, 'isError')) {
        this.toastService.success({ message: this.translateService.instant('text.dms.ZipFolderUploadSuccess') });
        this.closeWithResult(null);
      }
    };
  }

  uploadZipFolder(): void {
    if (this.folderCommentControl.value) {
      this.refreshAllUrls();
    }

    this.dmsFilesSandbox.addUploadingFiles(this.uploader.getNotUploadedItems() as FileItem[]);
    this.uploader.uploadAll();
  }

  refreshAllUrls(): void {
    this.uploader.queue.forEach(file => {
      if (this.folderCommentControl.value) {
        file.url += `?folderComment=${encodeURIComponent(this.folderCommentControl.value)}`;
      }
    });
  }

  getItemStatusTooltip(item: FileItem): string {
    if (item.isSuccess) {
      return 'text.dms.SuccessUploadingFile';
    }
    if (item.isError) {
      return 'text.dms.ErrorUploadingFile';
    }
    return '';
  }
}
